import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CandidateLinkShell, CandidateLinkSpinner, ExpiredLink } from '../components/CandidateLinkShell'
import { candidatePrivacyNoticePath, configureCandidateLinkApi } from '../services/candidateLinkRoute'
import {
  Certifications,
  acceptCertificationsConsent,
  getCertifications,
  uploadCertifications,
} from '../services/candidateLinks'

type Status = 'loading' | 'ready' | 'gone'
const PRIVACY_NOTICE_VERSION = '2026-09-18'

/** A compatibility page for an unexpired certifications link from the split flow. */
const CandidateCertifications: React.FC = () => {
  const { region, code } = useParams<{ region: string; code: string }>()
  const [status, setStatus] = useState<Status>('loading')
  const [state, setState] = useState<Certifications | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    if (!code || !configureCandidateLinkApi(region)) {
      setStatus('gone')
      return
    }
    try {
      setState(await getCertifications(code))
      setStatus('ready')
    } catch {
      setStatus('gone')
    }
  }, [code, region])

  useEffect(() => {
    load()
  }, [load])

  const accept = async () => {
    if (!code || busy) return
    setBusy(true)
    setError(null)
    try {
      setState(await acceptCertificationsConsent(code, PRIVACY_NOTICE_VERSION))
    } catch {
      setError('We could not save that just now. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const send = async (files: FileList | null) => {
    if (!code || !files?.length || busy) return
    setBusy(true)
    setError(null)
    try {
      setState(await uploadCertifications(code, Array.from(files)))
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'We could not send that. Please try again.')
    } finally {
      setBusy(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  if (status === 'loading') return <CandidateLinkShell><CandidateLinkSpinner /></CandidateLinkShell>
  if (status === 'gone' || !state) return <ExpiredLink code={code!} />

  if (!state.consent_given) {
    return (
      <CandidateLinkShell>
        <h1 className="text-2xl">Before you send anything</h1>
        <p className="mt-4 text-base" style={{ color: 'var(--link-ink-soft)' }}>
          Your documents are stored securely and shared only with the employer you
          applied to. Nothing you send here goes anywhere else.
        </p>
        <a
          href={candidatePrivacyNoticePath(region, code!)}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-block text-base underline"
          style={{ color: 'var(--link-action)' }}
        >
          Read the privacy notice
        </a>
        {error && <p className="mt-4 text-sm" style={{ color: '#a33' }}>{error}</p>}
        <button
          type="button"
          onClick={accept}
          disabled={busy}
          className="orbio-link-button mt-8 w-full rounded-xl px-4 py-3.5 text-base font-medium"
        >
          {busy ? 'One moment…' : 'I agree, continue'}
        </button>
      </CandidateLinkShell>
    )
  }

  return (
    <CandidateLinkShell>
      <h1 className="text-2xl">Your certifications</h1>
      {state.sent.length > 0 && (
        <ul className="mt-8 space-y-2">
          {state.sent.map((file) => (
            <li key={file.uuid} className="rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--link-good-ground)', color: 'var(--link-good)' }}>
              ✓ {file.name}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="mt-4 text-sm" style={{ color: '#a33' }}>{error}</p>}
      <input ref={fileInput} id="certifications" type="file" multiple className="sr-only" onChange={(event) => send(event.target.files)} />
      <label htmlFor="certifications" className="orbio-link-button mt-8 block w-full cursor-pointer rounded-xl px-4 py-3.5 text-center text-base font-medium" style={busy ? { background: 'var(--link-action-muted)' } : undefined}>
        {busy ? 'Sending…' : state.sent.length > 0 ? 'Add more' : 'Choose files'}
      </label>
    </CandidateLinkShell>
  )
}

export default CandidateCertifications
